import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import type { Applicant } from '../../types/applicant';

interface SendOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicant: Applicant | null;
  brandId: string;
  orgId: string;
  onSuccess: () => void;
}

export function SendOfferToApplicantModal({ isOpen, onClose, applicant, brandId, orgId, onSuccess }: SendOfferModalProps) {
  const [retainerAmount, setRetainerAmount] = useState('');
  const [commission, setCommission] = useState('25');
  const [postsPerMonth, setPostsPerMonth] = useState('30');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!retainerAmount || !commission || !postsPerMonth || !applicant) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/applicants/${applicant.id}/offer`, {
        method: 'POST',
        token,
        body: JSON.stringify({
          retainerAmount: parseFloat(retainerAmount),
          commission: parseFloat(commission),
          postsPerMonth: parseInt(postsPerMonth)
        })
      });
      
      toast.success('Offer sent successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to send offer:', error);
      toast.error('Failed to send offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Send Offer</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 block mb-2">
              Monthly Retainer Amount ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={retainerAmount}
              onChange={(e) => setRetainerAmount(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter amount"
              required
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 block mb-2">
              Commission (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter commission percentage"
              required
              min="0"
              max="100"
              step="0.1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 block mb-2">
              Posts Per Month <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={postsPerMonth}
              onChange={(e) => setPostsPerMonth(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter number of posts"
              required
              min="1"
              step="1"
            />
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-gray-100 rounded hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 